<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { defineEmits, inject, Ref } from 'vue'
import { useRozpocetStore } from '/@src/stores/rozpocet'
import { DruhVydavku } from '/@src/stores/rozpocet/types'
import { computed } from 'vue-demi'
import { Dokument } from '/@src/client-portal'
import { useFormValues } from 'vee-validate'
import { useZoppm } from '/@src/composable/useZoppm'
import { useNotyf } from '/@src/composable/useNotyf'
const { t } = useI18n()

export interface VydavokProjektuProps {
  /** Id polozky v Pinii.*/
  polozkaId: string
  canRemoveExpense?: boolean
  druhPolozky: DruhVydavku
  zdrojeFinancovania: object
}

export interface VydavokProjektuEmits {
  /** Emit vyvolaný po kliknutí na odstránenie konkrétneho výdavku. */
  (e: 'remove', id: string): void
}

const emit = defineEmits<VydavokProjektuEmits>()
const props = withDefaults(defineProps<VydavokProjektuProps>(), {
  canRemoveExpense: false,
})

const rozpocetStore = useRozpocetStore()
const vydavok = rozpocetStore.getPolozka(props.druhPolozky, props.polozkaId)!

const isReadOnly = inject<boolean>('isReadOnly')

// zober nazov sekcie
const nazovVydavkuValue = computed(() => vydavok.nazov)
const kategoriaVydavku = computed(() => vydavok.kategoriaVydavku)

const prilohyIds = computed(() =>
  rozpocetStore.prilohyIds(props.druhPolozky, props.polozkaId)
)

const handleRemovePrilohy = (prilohaId: string) => {
  if (kodIrz?.value && confirm('Naozaj si želáte odstrániť prílohu?')) {
    rozpocetStore.removePriloha(
      props.druhPolozky,
      props.polozkaId,
      prilohaId,
      kodIrz.value
    )
  }
}

const handlePridatPrilohu = () => {
  rozpocetStore.createNewPriloha(props.druhPolozky, props.polozkaId)
}

const getDokumentyForPriloha = rozpocetStore.dokumentyForPrilohaGetter(props.druhPolozky)
const removeDokumentFromPriloha = rozpocetStore.removeDokumentFromPriloha(
  props.druhPolozky
)

const handleAddDokument = (prilohaId: string, dokument: Required<Dokument>) => {
  rozpocetStore.addDokumentToPriloha(
    props.druhPolozky,
    props.polozkaId,
    prilohaId,
    dokument
  )
}

const kodIrz = inject<Ref<string>>('kodIrz')
const zoppm = useZoppm()
const formValues = useFormValues()
const notif = useNotyf()

// TODO: tieto handlere by sa dali upratat do VydavokPrilRozpocet.vue
const handleRemoveDokument = async (prilohaId: string, dokumentId: string) => {
  if (removeDokumentFromPriloha && kodIrz?.value) {
    try {
      await removeDokumentFromPriloha(
        props.polozkaId,
        prilohaId,
        dokumentId,
        kodIrz.value
      )
      notif.success('Súbor bol zmazaný.')
    } catch (e) {
      notif.error(e as string)
    } finally {
      await zoppm?.handleUpdateDraft(kodIrz!.value, formValues.value)
    }
  }
}
</script>

<template>
  <slot name="divider" />

  <IDSKHeading as="h3" variant="m">
    {{ t('zoppm.step9.form.vydavok') }}
  </IDSKHeading>
  <IDSKInput v-model="vydavok.nazov" :label="t('zoppm.step9.form.titleField')" />
  <VydavokProjektuZdrojeFinancovania
    :polozka-id="polozkaId"
    :druh-polozky="druhPolozky"
    :zdroje-financovania="props.zdrojeFinancovania"
  />

  <IDSKHeading as="h3" variant="m">{{ t('zoppm.step9.attachmentTitle') }}</IDSKHeading>
  <IDSKParagraph>{{ t('zoppm.step9.attachmentInfo') }}</IDSKParagraph>

  <template v-if="!isReadOnly">
    <template v-for="prilohaId in prilohyIds" :key="prilohaId">
      <div class="govuk-inset-text">
        <ValidationWrapper
          v-slot="slotProps"
          :name="`priloha.lokalnaReferencia`"
          :value="{
            refSekcia: nazovVydavkuValue,
            refId: kategoriaVydavku,
          }"
        >
          <IDSKInput
            :id="`priloha.lokalnaReferencia`"
            v-bind="slotProps"
            readonly
            hidden
          />
        </ValidationWrapper>
        <VydavokProjektuPrilohy
          :name="`priloha`"
          :array-position="0"
          :polozka-index="0"
          :files-list="getDokumentyForPriloha(props.polozkaId, prilohaId)"
          @add="(dokument) => handleAddDokument(prilohaId, dokument)"
          @remove="(dokumentId) => handleRemoveDokument(prilohaId, dokumentId)"
        />
        <IDSKLink
          class="mb-6"
          href="#"
          no-visited-state
          @click.prevent="handleRemovePrilohy(prilohaId)"
        >
          {{ t('globalne.odstranitPrilohu') }}
        </IDSKLink>
      </div>
    </template>
    <IDSKButton
      v-if="prilohyIds.length < 1"
      variant="secondary"
      :data-test-id="`zoppm-vydavok-projektu-pridat-prilohu`"
      @click.prevent="handlePridatPrilohu"
    >
      {{ t('globalne.pridatPrilohu') }}
    </IDSKButton>
  </template>
  <div v-else>
    <UploadedFilesTable />
    <FileUploadButton variant="secondary">
      {{ t('zoppm.step9.addAttachment') }}
    </FileUploadButton>
  </div>

  <div v-if="props.canRemoveExpense" class="mb-8">
    <IDSKLink
      href="#"
      role="button"
      no-visited-state
      @click.prevent="() => emit('remove', polozkaId)"
    >
      {{ t('zoppm.step9.removeExpense') }}
    </IDSKLink>
  </div>
</template>

<style scoped lang="scss"></style>
